import React, { FC, useState } from 'react';
import { WeekBreakdownItem } from '../../../../../models/stats.models';
import { formatApiDateDisplay } from '../../../../../components/period-summary/period-summary.helpers';
import { getMeasurementUnit } from '../../../../../helpers/general.helpers';
import { formatSeasonDurationHours } from '../../../training-log.helpers';
import LogDayRow from './log-day-row.component';
import LogWeekComparison from './log-week-comparison.component';
import {
  buildWeekComparisonMetrics,
  buildWeekTeamChips,
  getPreviousWeek,
  getWeekCardStatus,
  groupTrainingsByDay,
} from '../log.helpers';

interface LogWeekCardProps {
  week: WeekBreakdownItem;
  allWeeks: WeekBreakdownItem[];
}

const LogWeekCard: FC<LogWeekCardProps> = ({ week, allWeeks }) => {
  const [bodyCollapsed, setBodyCollapsed] = useState(false);
  const distanceUnit = getMeasurementUnit();
  const weekStatus = getWeekCardStatus(week);
  const previousWeek = getPreviousWeek(allWeeks, week);
  const comparisonMetrics = buildWeekComparisonMetrics(week, previousWeek);
  const teamChips = buildWeekTeamChips(week.trainingList ?? []);
  const daySlots = groupTrainingsByDay(week.week, week.trainingList ?? [], weekStatus);

  const statusClass = weekStatus === 'current' ? 'log-week-current' : weekStatus === 'future' ? 'log-week-future' : '';

  return (
    <div className={`log-week-card ${statusClass}`}>
      <div
        className="log-week-card-header"
        onClick={() => setBodyCollapsed((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setBodyCollapsed((v) => !v)}
      >
        <div>
          <div className="log-week-card-title">
            Week {week.index} — {formatApiDateDisplay(week.week)}
            {weekStatus === 'current' && <span className="log-week-current-badge">Current Week</span>}
            {weekStatus === 'future' && <span className="log-week-upcoming-label">Upcoming</span>}
          </div>
        </div>
        <div className="log-week-card-meta">
          <div className="log-week-card-sess">{week.trainings?.total ?? 0} sessions</div>
          <div className="log-week-card-dist">
            {Math.round(week.distance?.total ?? 0)}
            {distanceUnit}
          </div>
          <div className="log-week-card-hours">{formatSeasonDurationHours(week.duration?.total ?? 0)}</div>
        </div>
      </div>

      {!bodyCollapsed && (
        <>
          {(week.trainings?.total ?? 0) > 0 && <LogWeekComparison metrics={comparisonMetrics} />}

          {teamChips.length > 0 && (
            <div className="log-week-team-chips">
              {teamChips.map((chip) => (
                <div key={chip.key} className="log-week-team-chip" style={{ background: chip.theme.cardBg }}>
                  <div className="log-week-team-avatar" style={{ background: chip.theme.avatarBg }}>
                    <span>{chip.initials}</span>
                  </div>
                  <span className="log-week-team-name" style={{ color: chip.theme.avatarBg }}>
                    {chip.teamName}
                  </span>
                  <span className="log-week-team-count">{chip.count} sess</span>
                </div>
              ))}
            </div>
          )}

          <div className="log-week-card-body">
            {daySlots.map((slot) => (
              <LogDayRow key={`${week.week}-${slot.dayLabel}`} slot={slot} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LogWeekCard;
